/**
* Database Context for Zenda
*
* Provides reactive state management using Supabase.
* Components can subscribe to changes automatically via Supabase real-time subscriptions.
*/

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { Task, TaskDoc } from '@/db';
import type { Priority } from '@/types';
import { isCompletedStatus, normalizeStatus, TASK_STATUS, DEFAULT_TASK_STATUS } from '@/constants/taskStatuses';
import {
  CACHE_MAX_AGE_MS,
  PERIODIC_SYNC_INTERVAL_MS,
  TASK_PAGE_LIMIT,
  REALTIME_INSERT_DEBOUNCE_MS,
} from '@/constants/timings';
import {
  convertTaskFromSupabase,
  createTask as dbCreateTask,
  updateTask as dbUpdateTask,
  deleteTask as dbDeleteTask,
  bulkUpdateTasks as dbBulkUpdateTasks,
  bulkDeleteTasks as dbBulkDeleteTasks,
  getStats,
} from '@/db';
import {
  getCachedTasks,
  syncAllTasksFromSupabase,
  isIndexedDBAvailable,
  getCacheMetadata,
  applyDeltaUpdates,
  removeTasksFromCache,
} from '@/utils/taskCache';
import {
  createUndoBulkUpdate,
  createUndoBulkDelete,
  showUndoToast,
} from '@/utils/undoHelpers';
import { handleError } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { useAuth } from '@/context/AuthContext';
import {
  logTaskCreated,
  logStatusChange,
  logAssigneeAdded,
  logAssigneeRemoved,
  logPriorityChanged,
  logDueDateChanged,
  logActivity,
} from '@/db/activityLog';
import {
  notifyAssignment,
  notifyStatusChange as notifyStatusChanged,
} from '@/db/notifications';
import { runAutomationsForTrigger } from '@/db/automations';

// Task event type from task_events table
interface TaskEventRow {
  id: string;
  type: 'upsert' | 'delete';
  task_id?: string;
  task?: TaskDoc;
  ts?: number;
}

interface TaskEvent {
  eventId?: string;
  type: 'upsert' | 'delete';
  taskId?: string;
  task?: Task & { id: string };
  ts?: number;
}

interface DatabaseContextValue {
  // State
  tasks: Array<Task & { id: string }>;
  loading: boolean;
  error: string | null;

  // CRUD Operations
  createTask: (data: Partial<Omit<Task, 'id'>>) => Promise<Task & { id: string }>;
  updateTask: (id: string, updates: Partial<Omit<Task, 'id'>>) => Promise<(Task & { id: string }) | null>;
  deleteTask: (id: string) => Promise<boolean>;
  bulkUpdateTasks: (ids: string[], updates: Partial<Omit<Task, 'id'>>) => Promise<Array<Task & { id: string }>>;
  bulkDeleteTasks: (ids: string[]) => Promise<number>;

  // Quick actions
  toggleTaskStatus: (id: string) => Promise<(Task & { id: string }) | null>;
  setTaskPriority: (id: string, priority: Priority | null) => Promise<(Task & { id: string }) | null>;
  addAssignee: (id: string, assignee: string) => Promise<(Task & { id: string }) | null>;
  removeAssignee: (id: string, assignee: string) => Promise<(Task & { id: string }) | null>;
  addTag: (id: string, tag: string) => Promise<(Task & { id: string }) | null>;
  removeTag: (id: string, tag: string) => Promise<(Task & { id: string }) | null>;

  // Utilities
  getTask: (id: string) => (Task & { id: string }) | null;
  resetDatabase: () => Promise<void>;
  stats: ReturnType<typeof getStats>;
}

// In dev, Vite HMR can reload this module and recreate the context object.
// If App.tsx still holds an older Provider reference, hooks would read from a different context
// instance and crash ("useDatabase must be used within a DatabaseProvider").
// Keep a single context instance across HMR reloads.
const __global = globalThis as unknown as {
  __ZendaDatabaseContext?: React.Context<DatabaseContextValue | null>;
};
const DatabaseContext = __global.__ZendaDatabaseContext ?? createContext<DatabaseContextValue | null>(null);
if (import.meta.env.DEV) {
  __global.__ZendaDatabaseContext = DatabaseContext;
}

export function DatabaseProvider({ children }: { children: React.ReactNode }) {
  // Cache state
  const [cachedTasks, setCachedTasks] = useState<Array<Task & { id: string }> | null>(null);
  const [cacheReady, setCacheReady] = useState(false); // we've attempted to read IndexedDB at least once
  const syncInProgressRef = useRef(false); // Prevent multiple simultaneous syncs
  const lastAppliedTaskEventTsRef = useRef<number>(0);
  const processedEventIdsRef = useRef<Set<string>>(new Set()); // Deduplication: track applied event IDs
  const [firstBatch, setFirstBatch] = useState<Array<Task & { id: string }> | null>(null);

  // Current user for activity logging and notifications (ref avoids re-creating callbacks)
  const { user } = useAuth();
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  // Ref to current tasks array so callbacks can read the latest value
  // without listing `tasks` as a dependency (which would recreate every callback on each change).
  const tasksRef = useRef<Array<Task & { id: string }>>([])

  // Load from cache on mount
  useEffect(() => {
    if (isIndexedDBAvailable()) {
      const cacheLoadStartTime = performance.now();
      Promise.all([getCachedTasks(), getCacheMetadata()])
        .then(([tasks, metadata]) => {
          const cacheLoadTime = performance.now() - cacheLoadStartTime;
          logger.debug(`[Cache] Tasks loaded from cache in ${cacheLoadTime.toFixed(2)}ms (${tasks.length} tasks)`);

          // Establish a baseline for delta sync (avoid replaying old events).
          // Use lastDeltaSyncTime if present, otherwise lastSyncTime, otherwise 0.
          lastAppliedTaskEventTsRef.current =
            metadata?.lastDeltaSyncTime ?? metadata?.lastSyncTime ?? 0;

          // Keep behavior: only set cachedTasks if we have data; otherwise let app fall back to firstBatch.
          if (tasks.length > 0) {
            setCachedTasks(tasks);
          }
          setCacheReady(true);
        })
        .catch((cacheError) => {
          handleError(cacheError, { level: 'warning', message: 'Errore nel caricamento dei task dalla cache' });
          setCacheReady(true);
        });
    }
  }, []);

  // Start background sync to fetch all tasks
  useEffect(() => {
    // Prevent multiple simultaneous syncs
    if (syncInProgressRef.current) {
      return;
    }

    // Check if we need to sync (cache is empty, old, or incomplete)
    const startSync = async () => {
      if (!isIndexedDBAvailable()) return;

      const metadata = await getCacheMetadata();
      const cacheAge = metadata ? Date.now() - metadata.lastSyncTime : Infinity;

      // Get current cached tasks count
      const currentCachedTasks = await getCachedTasks();
      const cachedTaskCount = currentCachedTasks?.length || 0;

      // Check if we need a full sync
      let shouldSync = false;
      if (!metadata || cacheAge > CACHE_MAX_AGE_MS) {
        shouldSync = true;
      } else if (cachedTaskCount === 8000) {
        // Cache has exactly 8000 tasks — likely truncated by old page limit.
        // Force a full sync to ensure we have the complete dataset.
        shouldSync = true;
      }

      if (shouldSync) {
        syncInProgressRef.current = true;
        try {
          logger.debug('[Cache] Starting full sync from Supabase...');
          await syncAllTasksFromSupabase();
          // Reload from cache after sync
          const updatedTasks = await getCachedTasks();
          logger.debug(`[Cache] Full sync completed (${updatedTasks.length} tasks)`);
          processedEventIdsRef.current.clear(); // Clear dedup set on full sync
          setCachedTasks(updatedTasks);
        } catch (syncError) {
          // Log error but don't block the app - use cached data or firstBatch instead
          handleError(syncError, { level: 'warning', message: 'Errore nella sincronizzazione dei task' });
          // Set cachedTasks to empty array if sync fails and cache is empty.
          // Use functional form to read current value (avoids stale closure).
          setCachedTasks((prev) => prev ?? []);
        } finally {
          syncInProgressRef.current = false;
        }
      }
    };

    startSync();

    // Periodic full sync as safety net
    const intervalId = setInterval(() => {
      if (!syncInProgressRef.current) {
        startSync();
      }
    }, PERIODIC_SYNC_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  // Load first batch from Supabase (for reactive updates)
  useEffect(() => {
    const loadFirstBatch = async () => {
      try {
        const { data: tasks, error: fetchError } = await supabase
          .from('tasks')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(TASK_PAGE_LIMIT);

        if (fetchError) throw fetchError;
        const convertedTasks = (tasks || []).map(convertTaskFromSupabase);
        setFirstBatch(convertedTasks);
      } catch (loadError) {
        handleError(loadError, { level: 'error', message: 'Errore nel caricamento iniziale dei task' });
        setDbError('Errore nel caricamento dei task. Verifica la connessione.');
      }
    };
    loadFirstBatch();
  }, []);

  // Set up real-time subscription for tasks table (handles INSERT, UPDATE, and DELETE)
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const reloadFirstBatch = async () => {
      const { data: tasks, error: reloadError } = await supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(TASK_PAGE_LIMIT);

      if (!reloadError && tasks) {
        const convertedTasks = (tasks as TaskDoc[]).map(convertTaskFromSupabase);
        setFirstBatch(convertedTasks);
      }
    };

    const handleTaskChange = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(reloadFirstBatch, REALTIME_INSERT_DEBOUNCE_MS);
    };

    const channel = supabase
      .channel('tasks')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tasks',
        },
        handleTaskChange
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tasks',
        },
        (payload) => {
          // For UPDATE events, apply the change directly to avoid full reload
          const updatedTask = convertTaskFromSupabase(payload.new as TaskDoc);

          // Update firstBatch optimistically
          setFirstBatch((prev) => {
            if (!prev) return prev;
            const idx = prev.findIndex((t) => t.id === updatedTask.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = updatedTask;
              return next;
            }
            return prev;
          });

          // Also update cachedTasks for consistency
          setCachedTasks((prev) => {
            if (!prev) return prev;
            const idx = prev.findIndex((t) => t.id === updatedTask.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = updatedTask;
              return next;
            }
            return prev;
          });

          // Persist to IndexedDB
          if (isIndexedDBAvailable()) {
            applyDeltaUpdates([updatedTask]).catch((err) => logger.warn('Cache update failed:', err));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'tasks',
        },
        (payload) => {
          const deletedId = (payload.old as Partial<TaskDoc>)?.id;
          if (deletedId) {
            // Remove from firstBatch optimistically
            setFirstBatch((prev) => prev?.filter((t) => t.id !== deletedId) ?? null);

            // Also remove from cachedTasks
            setCachedTasks((prev) => prev?.filter((t) => t.id !== deletedId) ?? null);

            // Remove from IndexedDB
            if (isIndexedDBAvailable()) {
              removeTasksFromCache([deletedId]).catch((err) => logger.warn('Cache update failed:', err));
            }
          }
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  // Note: cachedTasks and firstBatch are used directly as memo dependencies below.
  // Previous hash-based stabilization was removed because it prevented optimistic
  // updates (e.g. DnD status changes) from propagating — the hash only captured
  // partial data and missed field-level changes within existing tasks.

  const applyTaskEventsToStateAndCache = useCallback(
    async (events: TaskEvent[]) => {
      if (!events || events.length === 0) return;
      if (!isIndexedDBAvailable()) return;

      // Normalize events -> upserts + deletes
      const upserts: Array<Task & { id: string }> = [];
      const deletes: string[] = [];

      for (const ev of events) {
        if (ev?.type === 'upsert' && ev?.task?.id) {
          upserts.push({ ...ev.task, id: ev.task.id });
        } else if (ev?.type === 'delete' && ev?.taskId) {
          deletes.push(String(ev.taskId));
        }
      }

      // Persist to IndexedDB (source of truth for rehydration)
      if (upserts.length > 0 || deletes.length > 0) {
        try {
          await applyDeltaUpdates(upserts, deletes.length > 0 ? deletes : undefined);
        } catch (deltaError) {
          handleError(deltaError, { level: 'warning', message: 'Errore nell\'applicazione degli aggiornamenti delta' });
        }
      }

      // Update in-memory cachedTasks to reflect changes immediately (avoid reloading all tasks from IndexedDB)
      setCachedTasks((prev) => {
        if (!prev) return prev;
        let next = prev;

        if (deletes.length > 0) {
          const deleteSet = new Set(deletes);
          next = next.filter((t) => !deleteSet.has(String(t.id)));
        }

        if (upserts.length > 0) {
          const map = new Map<string, Task & { id: string }>(next.map((t) => [String(t.id), t]));
          for (const t of upserts) {
            map.set(String(t.id), t as Task & { id: string });
          }
          next = Array.from(map.values());
          next.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));
        }

        return next;
      });
    },
    [],
  );

  // One-time catch-up: fetch all taskEvents since last applied timestamp.
  useEffect(() => {
    if (!isIndexedDBAvailable()) return;
    // Wait until we at least tried to load cache
    if (!cacheReady) return;

    let cancelled = false;

    const runCatchUp = async () => {
      try {
        const metadata = await getCacheMetadata();
        let since = metadata?.lastDeltaSyncTime ?? metadata?.lastSyncTime ?? 0;
        since = Math.max(since, lastAppliedTaskEventTsRef.current);

        const LIMIT = TASK_PAGE_LIMIT;
        while (!cancelled) {
          const { data: events, error: catchUpError } = await supabase
            .from('task_events')
            .select('*')
            .gt('ts', since)
            .order('ts', { ascending: true })
            .limit(LIMIT);

          if (catchUpError) throw catchUpError;
          if (!events || events.length === 0) break;

          // Deduplicate: skip events already processed
          const unseenEvents = events.filter((e: TaskEventRow) => !processedEventIdsRef.current.has(e.id));
          if (unseenEvents.length === 0) {
            // All events in this page were duplicates; advance cursor and continue
            const pageMaxTs = Math.max(...events.map((e) => Number(e?.ts ?? 0)));
            since = Math.max(since, pageMaxTs);
            lastAppliedTaskEventTsRef.current = since;
            if (events.length < LIMIT) break;
            continue;
          }

          const convertedEvents = unseenEvents.map((e: TaskEventRow) => ({
            eventId: e.id,
            type: e.type,
            taskId: e.task_id,
            task: e.task ? convertTaskFromSupabase(e.task) : undefined,
            ts: e.ts,
          }));

          await applyTaskEventsToStateAndCache(convertedEvents);

          // Mark as processed
          unseenEvents.forEach((e: TaskEventRow) => processedEventIdsRef.current.add(e.id));

          // Trim dedup set to prevent unbounded memory growth (sliding window)
          if (processedEventIdsRef.current.size > 1000) {
            const ids = Array.from(processedEventIdsRef.current);
            processedEventIdsRef.current = new Set(ids.slice(-500));
          }

          const maxTs = Math.max(...events.map((e) => Number(e?.ts ?? 0)));
          since = Math.max(since, maxTs);
          lastAppliedTaskEventTsRef.current = since;
          if (events.length < LIMIT) break;
        }
      } catch (catchUpErr) {
        handleError(catchUpErr, { level: 'warning', message: 'Errore nel recupero degli eventi task' });
      }
    };

    runCatchUp();
    return () => {
      cancelled = true;
    };
  }, [cacheReady, applyTaskEventsToStateAndCache]);

  // Combine cached tasks with Supabase reactive updates
  // Priority: Use cached tasks (complete dataset) merged with Supabase updates (for real-time changes)
  const tasks: Array<Task & { id: string }> = useMemo(() => {
    // Start with cached tasks if available (complete dataset)
    const baseTasks = cachedTasks || [];

    // Create a map of cached tasks by _id for quick lookup
    const cachedTasksMap = new Map<string, Task & { id: string }>();
    baseTasks.forEach(task => {
      cachedTasksMap.set(task.id, task);
    });

    // Merge in Supabase updates (firstBatch) for real-time updates.
    // Compare updated_at timestamps: keep whichever version is newer to avoid
    // firstBatch (which may be stale) overwriting more recent cached data.
    if (firstBatch) {
      firstBatch.forEach((task) => {
        const taskId = String(task.id);
        const existing = cachedTasksMap.get(taskId);
        if (existing) {
          // Keep the version with the more recent updated_at timestamp
          const existingTs = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
          const incomingTs = task.updatedAt ? new Date(task.updatedAt).getTime() : 0;
          if (incomingTs > existingTs) {
            cachedTasksMap.set(taskId, { ...task, id: taskId });
          }
          // else: cache version is newer, keep it
        } else {
          // Task not in cache, add it
          cachedTasksMap.set(taskId, { ...task, id: taskId });
        }
      });
    }

    // Convert map back to array, ensuring no duplicates, and sort by dateCreated descending
    const mergedTasks = Array.from(cachedTasksMap.values());
    mergedTasks.sort((a, b) => (b.dateCreated || 0) - (a.dateCreated || 0));

    return mergedTasks;
  }, [cachedTasks, firstBatch]);

  // Keep tasksRef in sync so stable callbacks can read the latest tasks.
  tasksRef.current = tasks;

  // Loading state: true if cache is empty AND Supabase hasn't loaded yet
  const loading = cachedTasks === null && firstBatch === null;
  const [dbError, setDbError] = useState<string | null>(null);
  const error = dbError;

  // CRUD Operations (public API)
  const createTask = useCallback(
    async (data: Partial<Omit<Task, 'id'>>): Promise<Task & { id: string }> => {
      // Create optimistic task with camelCase fields only
      const now = Date.now();
      const tempId = `temp-${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const dateCreatedValue = typeof data.dateCreated === 'number' ? data.dateCreated : ((data.dateCreated as Date | undefined)?.getTime() ?? now);
      const dueDateValue = typeof data.dueDate === 'number' ? data.dueDate : ((data.dueDate as Date | undefined)?.getTime() ?? null);
      const startDateValue = typeof data.startDate === 'number' ? data.startDate : ((data.startDate as Date | undefined)?.getTime() ?? null);

      const tempTask: Task & { id: string } = {
        // Core fields
        id: tempId,
        name: data.name ?? 'Nuovo task',
        content: data.content ?? '',
        status: data.status ?? DEFAULT_TASK_STATUS,

        // Timestamps
        dateCreated: dateCreatedValue,
        dueDate: dueDateValue,
        startDate: startDateValue,

        // Relationships
        parentId: data.parentId ?? null,
        spaceId: data.spaceId ?? '',
        sprintId: data.sprintId ?? null,
        epicId: data.epicId ?? null,
        milestoneId: data.milestoneId ?? null,
        templateId: data.templateId ?? null,

        // Planning
        storyPoints: data.storyPoints ?? null,

        // Arrays
        attachments: data.attachments ?? [],
        assignees: data.assignees ?? [],
        tags: data.tags ?? [],

        // Optional fields
        priority: data.priority ?? null,
        timeEstimated: data.timeEstimated ?? null,
        timeEstimatesByAssignee: data.timeEstimatesByAssignee ?? null,
        rolledUpTime: data.rolledUpTime ?? null,
        checklists: data.checklists ?? {},
        comments: data.comments ?? [],
        assignedComments: data.assignedComments ?? 0,
        customFields: data.customFields ?? null,
        estimateHistory: data.estimateHistory ?? null,

        // Baseline
        baselineStartDate: data.baselineStartDate ?? null,
        baselineDueDate: data.baselineDueDate ?? null,

        // Scheduling
        scheduledStart: data.scheduledStart ?? null,
        scheduledEnd: data.scheduledEnd ?? null,

        // Ordering
        sortOrder: data.sortOrder ?? null,

        // Metadata
        createdAt: new Date(now).toISOString(),
        updatedAt: new Date(now).toISOString(),
      };

      // Optimistically add to in-memory cache so it shows up immediately
      setCachedTasks((prev) => (prev ? [tempTask, ...prev] : prev));

      // Create in Supabase; then replace optimistic task with the real one
      let created: Task & { id: string };
      try {
        created = await dbCreateTask(data);
      } catch (err) {
        // Rollback: remove optimistic temp task
        setCachedTasks((prev) => prev?.filter((t) => String(t.id) !== tempId) ?? null);
        if (isIndexedDBAvailable()) {
          removeTasksFromCache([tempId]).catch((err) => logger.warn('Cache update failed:', err));
        }
        handleError(err, { level: 'error', message: 'Errore nella creazione del task' });
        throw err;
      }

      setCachedTasks((prev) => {
        if (!prev) return prev;
        // Replace temp with created
        let next = prev.map((t) => (String(t.id) === tempId ? created : t));
        // Ensure created exists (prepend if not)
        if (!next.some((t) => String(t.id) === String(created.id))) {
          next = [created, ...next.filter((t) => String(t.id) !== tempId)];
        }
        // Drop any remaining temp task
        next = next.filter((t) => String(t.id) !== tempId);
        return next;
      });

      // Keep IndexedDB consistent
      if (isIndexedDBAvailable()) {
        applyDeltaUpdates([created], [tempId]).catch((err) => logger.warn('Cache update failed:', err));
      }

      // Fire-and-forget: log activity + run automations
      const uid = userIdRef.current;
      if (uid) {
        logTaskCreated(created.id, created.spaceId, uid, created.name)
          .catch(err => logger.warn('Activity log failed:', err));
      }
      runAutomationsForTrigger(created.spaceId, 'task_created', created.id, created as unknown as Record<string, unknown>)
        .catch(err => logger.warn('Automation trigger failed:', err));

      return created;
    },
    [],
  );

  const updateTask = useCallback(
    async (id: string, updates: Partial<Omit<Task, 'id'>>): Promise<(Task & { id: string }) | null> => {
      const existing = tasksRef.current.find((t) => t.id === id);
      if (!existing) return null;

      // 1. Snapshot before mutation
      const snapshot = { ...existing };

      // 2. Apply optimistic update
      // Normalize status if present in updates to ensure canonical values in cache.
      const normalizedUpdates = updates.status !== undefined
        ? { ...updates, status: normalizeStatus(updates.status as string) }
        : updates;
      // Set updatedAt so downstream hash-based change detection (useTaskEnrichment)
      // recognizes the task as changed and re-enriches it with the new field values.
      const updated: Task & { id: string } = { ...existing, ...normalizedUpdates, updatedAt: new Date().toISOString() };
      setCachedTasks((prev) => {
        if (!prev) return prev;
        return prev.map((t) => (t.id === id ? updated : t));
      });
      if (isIndexedDBAvailable()) {
        applyDeltaUpdates([updated]).catch((err) => logger.warn('Cache update failed:', err));
      }

      // 3. Call server
      try {
        await dbUpdateTask(id, updates);
      } catch (err) {
        // 4. Rollback on failure
        setCachedTasks((prev) => {
          if (!prev) return prev;
          return prev.map((t) => (t.id === id ? snapshot : t));
        });
        if (isIndexedDBAvailable()) {
          applyDeltaUpdates([snapshot]).catch((err) => logger.warn('Cache update failed:', err));
        }
        handleError(err, { level: 'error', message: 'Errore nell\'aggiornamento del task' });
        return null;
      }

      // Fire-and-forget: log activity, send notifications, and run automations
      const uid = userIdRef.current;
      if (uid) {
        if (updates.status !== undefined && updates.status !== snapshot.status) {
          logStatusChange(id, snapshot.spaceId, uid, snapshot.status, updates.status as string)
            .catch(err => logger.warn('Activity log failed:', err));
          const assignees = (updated.assignees || []) as string[];
          if (assignees.length > 0) {
            notifyStatusChanged(assignees, id, snapshot.name, snapshot.status, updates.status as string, uid)
              .catch(err => logger.warn('Notification failed:', err));
          }
          runAutomationsForTrigger(snapshot.spaceId, 'status_changed', id, updated as unknown as Record<string, unknown>, { oldStatus: snapshot.status, newStatus: updates.status })
            .catch(err => logger.warn('Automation trigger failed:', err));
        } else if (updates.priority !== undefined && updates.priority !== snapshot.priority) {
          logPriorityChanged(id, snapshot.spaceId, uid, snapshot.priority ?? null, (updates.priority as string) ?? null)
            .catch(err => logger.warn('Activity log failed:', err));
          runAutomationsForTrigger(snapshot.spaceId, 'priority_changed', id, updated as unknown as Record<string, unknown>, { oldPriority: snapshot.priority, newPriority: updates.priority })
            .catch(err => logger.warn('Automation trigger failed:', err));
        } else if (updates.dueDate !== undefined && updates.dueDate !== snapshot.dueDate) {
          logDueDateChanged(id, snapshot.spaceId, uid, snapshot.dueDate ?? null, (updates.dueDate as number) ?? null)
            .catch(err => logger.warn('Activity log failed:', err));
          runAutomationsForTrigger(snapshot.spaceId, 'due_date_changed', id, updated as unknown as Record<string, unknown>)
            .catch(err => logger.warn('Automation trigger failed:', err));
        } else if (updates.assignees !== undefined) {
          const oldAssignees = (snapshot.assignees || []) as string[];
          const newAssignees = (updates.assignees || []) as string[];
          const added = newAssignees.filter(a => !oldAssignees.includes(a));
          const removed = oldAssignees.filter(a => !newAssignees.includes(a));
          for (const a of added) {
            logAssigneeAdded(id, snapshot.spaceId, uid, a)
              .catch(err => logger.warn('Activity log failed:', err));
          }
          for (const a of removed) {
            logAssigneeRemoved(id, snapshot.spaceId, uid, a)
              .catch(err => logger.warn('Activity log failed:', err));
          }
          if (added.length > 0) {
            notifyAssignment(added, id, snapshot.name, uid)
              .catch(err => logger.warn('Notification failed:', err));
            runAutomationsForTrigger(snapshot.spaceId, 'assignee_changed', id, updated as unknown as Record<string, unknown>, { addedAssignee: added[0] })
              .catch(err => logger.warn('Automation trigger failed:', err));
          }
          if (removed.length > 0) {
            runAutomationsForTrigger(snapshot.spaceId, 'assignee_changed', id, updated as unknown as Record<string, unknown>, { removedAssignee: removed[0] })
              .catch(err => logger.warn('Automation trigger failed:', err));
          }
        } else {
          logActivity({ taskId: id, spaceId: snapshot.spaceId, userId: uid, action: 'task_updated' })
            .catch(err => logger.warn('Activity log failed:', err));
        }
      }

      return updated;
    },
    [],
  );

  const deleteTask = useCallback(
    async (id: string): Promise<boolean> => {
      // 1. Snapshot before mutation
      const snapshot = tasksRef.current.find((t) => t.id === id);

      // 2. Apply optimistic update
      if (isIndexedDBAvailable()) {
        removeTasksFromCache([id]).catch((err) => logger.warn('Cache update failed:', err));
      }
      setCachedTasks(prev => prev?.filter(t => t.id !== id) ?? null);

      // 3. Call server
      try {
        await dbDeleteTask(id);
      } catch (err) {
        // 4. Rollback on failure
        if (snapshot) {
          setCachedTasks((prev) => (prev ? [snapshot, ...prev] : [snapshot]));
          if (isIndexedDBAvailable()) {
            applyDeltaUpdates([snapshot]).catch((err) => logger.warn('Cache update failed:', err));
          }
        }
        handleError(err, { level: 'error', message: 'Errore nell\'eliminazione del task' });
        return false;
      }

      // Fire-and-forget: log activity
      const uid = userIdRef.current;
      if (uid && snapshot) {
        logActivity({ taskId: id, spaceId: snapshot.spaceId, userId: uid, action: 'task_deleted', newValue: snapshot.name })
          .catch(err => logger.warn('Activity log failed:', err));
      }

      return true;
    },
    [],
  );

  const bulkUpdateTasks = useCallback(
    async (ids: string[], updates: Partial<Omit<Task, 'id'>>): Promise<Array<Task & { id: string }>> => {
      // Guard against empty arrays
      if (ids.length === 0) {
        return [];
      }

      // Determine the type of update for undo tracking
      let undoType: 'bulk_status_change' | 'bulk_priority_change' = 'bulk_status_change';
      if (updates.priority !== undefined) {
        undoType = 'bulk_priority_change';
      }

      // Create undo action before applying updates
      const currentTasks = tasksRef.current;
      const undoId = createUndoBulkUpdate(ids, updates, currentTasks, undoType);

      // 1. Snapshot all affected tasks before mutation
      const snapshots = currentTasks.filter((t) => ids.includes(t.id)).map((t) => ({ ...t }));

      // 2. Apply optimistic update
      const normalizedBulkUpdates = updates.status !== undefined
        ? { ...updates, status: normalizeStatus(updates.status as string) }
        : updates;
      const idSet = new Set(ids);
      const updatedTasks = currentTasks.filter((t) => idSet.has(t.id)).map((t) => ({
        ...t,
        ...normalizedBulkUpdates,
        updatedAt: new Date().toISOString(),
      }));

      setCachedTasks((prev) => {
        if (!prev) return prev;
        const updateMap = new Map(updatedTasks.map((t) => [t.id, t]));
        return prev.map((t) => updateMap.get(t.id) || t);
      });
      if (isIndexedDBAvailable()) {
        applyDeltaUpdates(updatedTasks).catch((err) => logger.warn('Cache update failed:', err));
      }

      // 3. Call server
      try {
        await dbBulkUpdateTasks(ids, updates);
      } catch (err) {
        // 4. Rollback on failure
        setCachedTasks((prev) => {
          if (!prev) return prev;
          const snapshotMap = new Map(snapshots.map((s) => [s.id, s]));
          return prev.map((t) => snapshotMap.get(t.id) || t);
        });
        if (isIndexedDBAvailable()) {
          applyDeltaUpdates(snapshots).catch((err) => logger.warn('Cache update failed:', err));
        }
        handleError(err, { level: 'error', message: `Errore nell'aggiornamento di ${ids.length} task` });
        return [];
      }

      // Show toast with undo button (only on success)
      const actionType = undoType === 'bulk_status_change' ? 'status' : 'priority';
      showUndoToast(`Updated ${actionType} for ${ids.length} task(s)`, undoId);

      return updatedTasks;
    },
    [],
  );

  const bulkDeleteTasks = useCallback(
    async (ids: string[]): Promise<number> => {
      // Guard against empty arrays
      if (ids.length === 0) {
        return 0;
      }

      // 1. Snapshot all affected tasks before mutation
      const currentTasks = tasksRef.current;
      const snapshots = currentTasks.filter((t) => ids.includes(t.id)).map((t) => ({ ...t }));

      // Create undo action before deleting tasks
      const undoId = createUndoBulkDelete(ids, currentTasks);

      // 2. Apply optimistic update
      if (isIndexedDBAvailable()) {
        removeTasksFromCache(ids).catch((err) => logger.warn('Cache update failed:', err));
      }
      setCachedTasks(prev => prev?.filter(t => !ids.includes(t.id)) ?? null);

      // 3. Call server
      try {
        await dbBulkDeleteTasks(ids);
      } catch (err) {
        // 4. Rollback on failure
        setCachedTasks((prev) => (prev ? [...snapshots, ...prev] : snapshots));
        if (isIndexedDBAvailable()) {
          applyDeltaUpdates(snapshots).catch((err) => logger.warn('Cache update failed:', err));
        }
        handleError(err, { level: 'error', message: `Errore nell'eliminazione di ${ids.length} task` });
        return 0;
      }

      // Show toast with undo button (only on success)
      showUndoToast(`Deleted ${ids.length} task(s)`, undoId);
      return ids.length;
    },
    [],
  );

  // Quick actions (delegate to async updateTask)
  const toggleTaskStatus = useCallback(async (id: string): Promise<(Task & { id: string }) | null> => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task) return null;

    return updateTask(id, {
      status: isCompletedStatus(task.status) ? DEFAULT_TASK_STATUS : TASK_STATUS.COMPLETED,
    });
  }, [updateTask]);

  const setTaskPriority = useCallback(async (id: string, priority: Priority | null): Promise<(Task & { id: string }) | null> => {
    return updateTask(id, { priority });
  }, [updateTask]);

  const addAssignee = useCallback(async (id: string, assignee: string): Promise<(Task & { id: string }) | null> => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task) return null;

    const assignees = task.assignees || [];
    if (assignees.includes(assignee)) return task;

    return updateTask(id, {
      assignees: [...assignees, assignee],
    });
  }, [updateTask]);

  const removeAssignee = useCallback(async (id: string, assignee: string): Promise<(Task & { id: string }) | null> => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task) return null;

    const assignees = task.assignees || [];
    return updateTask(id, {
      assignees: assignees.filter(a => a !== assignee),
    });
  }, [updateTask]);

  const addTag = useCallback(async (id: string, tag: string): Promise<(Task & { id: string }) | null> => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task) return null;

    const tags = task.tags || [];
    if (tags.includes(tag)) return task;

    return updateTask(id, {
      tags: [...tags, tag],
    });
  }, [updateTask]);

  const removeTag = useCallback(async (id: string, tag: string): Promise<(Task & { id: string }) | null> => {
    const task = tasksRef.current.find((t) => t.id === id);
    if (!task) return null;

    const tags = task.tags || [];
    return updateTask(id, {
      tags: tags.filter(t => t !== tag),
    });
  }, [updateTask]);

  // Utilities
  const getTask = useCallback((id: string): (Task & { id: string }) | null => {
    return tasksRef.current.find((t) => t.id === id) ?? null;
  }, []);

  const resetDatabase = useCallback(async (): Promise<void> => {
    // Supabase real-time subscriptions automatically update on changes
    // No manual invalidation needed
  }, []);

  // Compute stats
  const stats = useMemo(() => getStats(tasks), [tasks]);

  const value: DatabaseContextValue = useMemo(() => ({
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    bulkUpdateTasks,
    bulkDeleteTasks,
    toggleTaskStatus,
    setTaskPriority,
    addAssignee,
    removeAssignee,
    addTag,
    removeTag,
    getTask,
    resetDatabase,
    stats,
  }), [
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    bulkUpdateTasks,
    bulkDeleteTasks,
    toggleTaskStatus,
    setTaskPriority,
    addAssignee,
    removeAssignee,
    addTag,
    removeTag,
    getTask,
    resetDatabase,
    stats,
  ]);

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
}

export function useDatabase() {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
}

// Export specific hooks for common operations
export function useTasks() {
  const { tasks, loading, error } = useDatabase();
  return { tasks, loading, error };
}

export function useTaskMutations() {
  const {
    createTask,
    updateTask,
    deleteTask,
    bulkUpdateTasks,
    bulkDeleteTasks,
    toggleTaskStatus,
    setTaskPriority,
    addAssignee,
    removeAssignee,
    addTag,
    removeTag,
  } = useDatabase();

  return {
    createTask,
    updateTask,
    deleteTask,
    bulkUpdateTasks,
    bulkDeleteTasks,
    toggleTaskStatus,
    setTaskPriority,
    addAssignee,
    removeAssignee,
    addTag,
    removeTag,
  };
}
